import productsJson from '@/content/products.json';
import { toCents } from '@/lib/money';
import type { PricedVariant, Product } from '@/types/catalog';

import { resolveProductImage } from './productImages';

/**
 * Catálogo da loja.
 *
 * Os dados moram em `src/content/products.json`, editável pelo painel em
 * /admin. Este módulo só adiciona tipagem e os índices de busca.
 *
 * Só entram os produtos ativos. Filtrar aqui, na origem, tira o inativo de
 * todo o site de uma vez: catálogo, filtros, contagem por marca, página do
 * produto e o carrinho (um SKU inativo deixa de ser encontrado).
 */
export const PRODUCTS = (productsJson as Product[]).filter((product) => product.active !== false);

/* ─────────────────────────────  Índices  ───────────────────────────── */

const PRODUCTS_BY_SLUG = new Map(PRODUCTS.map((product) => [product.slug, product]));

export interface SkuEntry {
  product: Product;
  variant: PricedVariant;
}

/**
 * SKU → produto + variante com preço em centavos.
 *
 * O carrinho guarda apenas SKUs, então preço e estoque são sempre lidos deste
 * arquivo — nunca do que ficou salvo no navegador do visitante meses atrás.
 */
const BY_SKU = new Map<string, SkuEntry>();

/** Variantes já convertidas para centavos, na mesma ordem do catálogo. */
const PRICED_VARIANTS = new WeakMap<Product, PricedVariant[]>();

for (const product of PRODUCTS) {
  const priced: PricedVariant[] = product.variants.map((variant) => ({
    ...variant,
    priceCents: toCents(variant.price),
    ...(variant.compareAtPrice !== undefined
      ? { compareAtPriceCents: toCents(variant.compareAtPrice) }
      : {}),
  }));

  PRICED_VARIANTS.set(product, priced);

  for (const variant of priced) {
    BY_SKU.set(variant.sku, { product, variant });
  }
}

/** Variantes do produto com os valores prontos para cálculo. */
export function getVariants(product: Product): PricedVariant[] {
  return PRICED_VARIANTS.get(product) ?? [];
}

export function getProductBySlug(slug: string): Product | undefined {
  return PRODUCTS_BY_SLUG.get(slug);
}

export function getBySku(sku: string): SkuEntry | undefined {
  return BY_SKU.get(sku);
}

/**
 * Variante que define o preço "a partir de" no card.
 *
 * Prefere a mais barata que dá para comprar: anunciar um preço de uma variante
 * esgotada leva o cliente para a página de produto e frustra a expectativa.
 */
export function getEntryVariant(product: Product): PricedVariant {
  const variants = getVariants(product);
  const available = variants.filter((variant) => variant.stock !== 'out-of-stock');
  const pool = available.length > 0 ? available : variants;

  return pool.reduce(
    (cheapest, variant) => (variant.priceCents < cheapest.priceCents ? variant : cheapest),
    pool[0]!,
  );
}

/** Seleção inicial na página de produto: o menor tamanho disponível. */
export function getDefaultVariant(product: Product): PricedVariant {
  const variants = getVariants(product);
  return variants.find((variant) => variant.stock !== 'out-of-stock') ?? variants[0]!;
}

export function findVariant(product: Product, sku: string): PricedVariant | undefined {
  return getVariants(product).find((variant) => variant.sku === sku);
}

/** `true` quando pelo menos uma variante pode ser vendida. */
export function isProductAvailable(product: Product): boolean {
  return product.variants.some((variant) => variant.stock !== 'out-of-stock');
}

/** Fotos resolvidas do produto, na ordem de exibição. */
export function getProductImages(product: Product): string[] {
  const references = product.images?.length ? product.images : product.image ? [product.image] : [];

  return references
    .map((reference) => resolveProductImage(reference))
    .filter((url): url is string => Boolean(url));
}
